import { h as __h } from "preact";
/** @import { ComponentChildren } from "preact"; */
import { Component, h } from "preact";
import { hostResponse } from "~/backend/validate.js";
import { ApiError, authedApiRequest, paginatedResponse, query } from "~/lib/api.js";
import { SUPPORT_EMAIL } from "~/lib/consts.js";
import { identity } from "~/lib/identity.js";
import html from "~/util/html.js";
import { modal } from "~/util/modal.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import { navigate } from "~/util/router.js";
import { object, parse, string } from "~/util/validate.js";
import Banner from "~/component/Banner.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import Icon from "~/component/Icon.js";
import adminStyle from "~/admin/lib/style.js";
import input from "~/style/input.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./createEnterprise.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./createEnterprise.module.css", import.meta.url);
const css = adopt(sheet);
class CreateEnterpriseError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        conflict: "You're already part of a business.",
        domain_claimed: "Your email domain is already managed by a business."
      }
    };
  }
}

/**
 * Creates an enterprise owned by the signed-in user. Once it exists, the page behind the modal
 * moves to the new admin panel's invites and the modal turns into a welcome. Owning an enterprise
 * replaces their personal account, so the form spells out what they'll lose.
 */
export const createEnterprise = () => modal({
  content: (/** @type {() => void} */resolve) => h(ModalChildren, {
    resolve
  })
});

/**
 * @extends {Component<ModalChildrenProps>}
 *
 * @typedef {object} ModalChildrenProps
 * @property {() => void} resolve
 */
class ModalChildren extends Component {
  hosts = query(() => ({
    key: ["/v2/hosts", {
      show_offline: true,
      limit: 100
    }]
  }), {
    schema: paginatedResponse(hostResponse)
  });
  formState = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    const form = new FormData(ev.currentTarget);
    const json = parse(object({
      name: string
    }), Object.fromEntries(form.entries()));
    try {
      await authedApiRequest("POST", "/v2/enterprises", {
        json
      });
    } finally {
      Query.invalidate();
    }
    // the admin panel sends people without an enterprise away, so it needs the new session first
    await identity.fetch();
    navigate("/invites");
    return json.name;
  });
  render() {
    const close = () => this.props.resolve();
    const created = this.formState.data;
    if (created) {
      return __h(ModalLayout, {
        title: created + " is ready!",
        blurb: "Invite your team now to give them access.",
        onClose: close
      }, __h("ul", {
        "class": css.links
      }, __h("li", {}, __h("a", {
        "class": css.link,
        href: "https://help.phaze.app/articles/9185871880-phaze-administration-onboarding-guide",
        target: "_blank",
        rel: "noopener noreferrer"
      }, __h("h3", {
        "class": css.featureTitle
      }, "Onboarding guide"), __h("p", {
        "class": css.featureText
      }, "Set up organizations, groups, relays, SSO and SCIM, step by step."), __h("span", {
        "class": css.linkArrow
      }, __h(Icon, {
        name: "forward",
        size: 16
      })))), __h("li", {}, __h("a", {
        "class": css.link,
        href: "https://apidocs.phaze.app",
        target: "_blank",
        rel: "noopener noreferrer"
      }, __h("h3", {
        "class": css.featureTitle
      }, "Phaze API"), __h("p", {
        "class": css.featureText
      }, "Automate and integrate Phaze into your systems."), __h("span", {
        "class": css.linkArrow
      }, __h(Icon, {
        name: "forward",
        size: 16
      })))), __h("li", {}, __h("a", {
        "class": css.link,
        href: "https://help.phaze.app/articles/6522930271-the-phaze-mcp-server",
        target: "_blank",
        rel: "noopener noreferrer"
      }, __h("h3", {
        "class": css.featureTitle
      }, "Phaze MCP"), __h("p", {
        "class": css.featureText
      }, "Give your AI agents access to your computers."), __h("span", {
        "class": css.linkArrow
      }, __h(Icon, {
        name: "forward",
        size: 16
      }))))), __h("p", {
        "class": css.blurb
      }, "If you need a hand, email us at ", __h("a", {
        href: "mailto:" + SUPPORT_EMAIL
      }, SUPPORT_EMAIL), "."), __h("div", {
        "class": adminStyle.actions
      }, __h(Button, {
        level: "primary",
        onClick: close
      }, "Invite your team")));
    }
    const error = this.formState.error && CreateEnterpriseError.from(this.formState.error);
    const hasComputers = !!this.hosts.data?.data.length;
    return __h(ModalLayout, {
      title: "Using Phaze for your business?",
      blurb: "Manage assignment and access to computers, set up API keys, integrate SSO and SCIM directory sync, view audit logs, enforce policies, assign roles, and more.",
      onClose: close,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.formState.invoke(ev);
      }
    }, __h("ul", {
      "class": css.features
    }, __h("li", {
      "class": css.feature
    }, __h("span", {
      "class": css.featureIcon
    }, __h(Icon, {
      name: "machine",
      size: 16
    })), __h("h3", {
      "class": css.featureTitle
    }, "Computer management"), __h("p", {
      "class": css.featureText
    }, "Assign workstations across your company and manage them all from one view.")), __h("li", {
      "class": css.feature
    }, __h("span", {
      "class": css.featureIcon
    }, __h(Icon, {
      name: "users",
      size: 16
    })), __h("h3", {
      "class": css.featureTitle
    }, "Team access"), __h("p", {
      "class": css.featureText
    }, "Map groups and roles to shared computers, synced directly from your IdP.")), __h("li", {
      "class": css.feature
    }, __h("span", {
      "class": css.featureIcon
    }, __h(Icon, {
      name: "antenna",
      size: 16
    })), __h("h3", {
      "class": css.featureTitle
    }, "On-prem relays"), __h("p", {
      "class": css.featureText
    }, "Keep traffic on your own network, with policies you control centrally."))), __h(Field, {
      "for": "name",
      label: "Business name",
      error: error?.fields.name?.message
    }, __h("input", {
      "class": input.input,
      id: "name",
      name: "name",
      required: true,
      minlength: "2",
      maxlength: "100",
      autofocus: true
    })), __h("p", {
      "class": adminStyle.error
    }, error?.message), hasComputers && [__h(Banner, {
      icon: __h(Icon, {
        name: "warning"
      }),
      kind: "warning"
    }, "This will remove all computers from your account."), __h("label", {
      "class": css.confirm
    }, __h("input", {
      type: "checkbox",
      required: true
    }), __h("span", {}, "I understand that my personal account will be replaced by the business."))], __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      type: "submit",
      level: "primary",
      loading: this.formState.loading
    }, "Set up your business"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      value: "cancel"
    }, "Cancel")));
  }
}

/**
 * @typedef {object} ModalLayoutProps
 * @property {string} title
 * @property {string} blurb
 * @property {() => void} onClose
 * @property {(ev: SubmitEvent & { currentTarget: HTMLFormElement }) => void} [onSubmit]
 * @property {ComponentChildren} [children]
 * @param {ModalLayoutProps} props
 */
function ModalLayout({
  title,
  blurb,
  onClose,
  onSubmit,
  children
}) {
  return __h("form", {
    "class": adminStyle.modal,
    onSubmit: onSubmit
  }, __h("div", {
    "class": css.hero
  }, __h("div", {
    "class": css.close
  }, __h(Button, {
    level: "tertiary",
    size: "small",
    onClick: onClose
  }, __h(Icon, {
    name: "ex",
    size: 12,
    label: "Close"
  })))), __h("div", {
    "class": css.heading
  }, __h("h2", {
    "class": css.title
  }, title), __h("p", {
    "class": css.blurb
  }, blurb)), children);
}